import { useState } from 'react'
import { CreditCard, CheckCircle2, AlertCircle, Clock, ShieldAlert, ArrowUpRight } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { useAdminStats } from '../../hooks/use-admin'
import { EnterpriseTable, type Column } from '../../components/tables/enterprise-table'
import type { AdminPayment } from '../../types/admin.types'

export function PaymentsPage() {
  const { data: stats, isLoading: isStatsLoading } = useAdminStats()

  // Sample or placeholder payments table until payment gateway integration is linked
  const [payments] = useState<AdminPayment[]>([])

  const columns: Column<AdminPayment>[] = [
    {
      key: 'transactionId',
      header: 'Transaction ID',
      sortable: true,
      accessor: (p) => <span className="font-mono text-xs font-bold text-primary">{p.transactionId}</span>,
    },
    {
      key: 'user',
      header: 'User',
      sortable: true,
      sortValue: (p) => `${p.user?.firstName ?? ''} ${p.user?.lastName ?? ''}`,
      accessor: (p) => (
        <div className="flex items-center gap-2.5">
          <div className="flex size-7 items-center justify-center rounded-lg bg-muted text-xs font-bold">
            {p.user?.firstName?.[0]}
          </div>
          <span className="font-semibold text-foreground">
            {p.user?.firstName} {p.user?.lastName}
          </span>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      sortValue: (p) => p.amount,
      accessor: (p) => (
        <span className="font-display font-bold text-foreground">
          ₹{p.amount.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      accessor: (p) => (
        <Badge
          variant={
            p.status === 'PAID'
              ? 'success'
              : p.status === 'PENDING'
              ? 'warning'
              : 'destructive'
          }
        >
          {p.status}
        </Badge>
      ),
    },
    {
      key: 'paymentMethod',
      header: 'Method',
      accessor: (p) => <span className="text-xs uppercase font-medium text-muted-foreground">{p.paymentMethod}</span>,
    },
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      sortValue: (p) => new Date(p.createdAt).getTime(),
      accessor: (p) => (
        <span className="text-xs text-muted-foreground">
          {new Date(p.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <SectionHeading
        eyebrow="Financial Audit"
        title="Payment Transactions"
        description="View transaction histories, platform revenue stats, gateway statuses, and settlement logs."
        align="left"
      />

      {/* Financial Overview Metrics */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Total Revenue
              </span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
                <CreditCard className="size-5" />
              </span>
            </div>
            <div className="mt-3">
              {isStatsLoading ? (
                <div className="h-8 w-28 animate-pulse rounded bg-muted/60" />
              ) : (
                <p className="font-display text-3xl font-extrabold text-foreground tracking-tight">
                  ₹{(stats?.payments.revenue ?? 0).toLocaleString('en-IN')}
                </p>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Settled payments total</p>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Successful Transactions
              </span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
                <CheckCircle2 className="size-5" />
              </span>
            </div>
            <div className="mt-3">
              {isStatsLoading ? (
                <div className="h-8 w-24 animate-pulse rounded bg-muted/60" />
              ) : (
                <p className="font-display text-3xl font-extrabold text-foreground tracking-tight">
                  {stats?.payments.paid ?? 0}
                </p>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Paid invoice receipts</p>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Razorpay Integration
              </span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
                <Clock className="size-5" />
              </span>
            </div>
            <div className="mt-3">
              <Badge variant="warning" className="text-xs font-bold uppercase">
                Placeholder Configured
              </Badge>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Keys configured in server env</p>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <EnterpriseTable
        data={payments}
        columns={columns}
        isLoading={false}
        searchPlaceholder="Search by transaction ID or user email..."
        emptyTitle="No payment transactions recorded yet"
        emptyDescription="Payment gateway integration placeholders are ready. Completed payments will appear here in real time."
      />
    </div>
  )
}
